import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Field, Form, Formik, yupToFormErrors } from 'formik';
import '../../../utils/yupIntlSetup';
import * as yup from 'yup';
import { isEmpty } from 'lodash';
// Components
import FormikPersist from '../../../components/fields/FormikPersist';
import SignaturePad from '../../../components/fields/SignaturePad';
import StepNavigation from '../../../components/StepNavigation';
import Loading from '../../../components/Loading';
import Card from '../../../components/Card';
import SubTitleWithIcon from '../../PersonalInfo/SubTitleWithIcon';
import StepTitle from '../../../components/StepTitle';
import UserIcon from '../../../assets/icons/user-icon.inline.svg';
import Alert from '../../../components/Alert';
import TrustBox from '../../../components/TrustBox';

// Utils
import {
  validateBirthday,
  validateFirstname,
  validateLastname,
} from '../../../utils/yupValidators';
import { useHandleError } from '../../../services/api';
import { handleSubmit } from './handleSubmit';

// Hooks
import {
  useBuild,
  useJsonApi,
} from '../../../services/GlobalStore/GlobalStore';
import { useLocalStorageState } from '../../../hooks/useLocalStorageState';
import useSortFields from '../../../hooks/useSortFields';
import useAuthenticate from '../../../hooks/useAuthenticate';

// Translations
import messages from './messages';
import PassengerDataForm from '../../../components/PassengerDataForm';
import PermissionDescription from '../../Permission/PermissionDescription';
import { PageType } from '../PassengerContainer';
import { differenceInYears, format, isValid, parse, parseISO } from 'date-fns';
import { steps } from '../../../utils/routes';
// import { PassengerModel } from '../../../models/PassengerModel';
import { PathDefs, useStepNavigate } from '../../../hooks/useStepNavigate';
import { PersonModel } from '../../../models/PersonModel';

// Models

export const fieldNameToValidationMap = {
  firstname: validateFirstname,
  first_name: validateFirstname,
  lastname: validateLastname,
  last_name: validateLastname,
  address: yup.string().required(),
  street_name: yup.string().required(),
  postal: yup.string().required(),
  phone_number: yup.string().required(),
  postal_code: yup.string().required(),
  city: yup.string().required(),
  country_id: yup.string().required(),
  birthday: validateBirthday.required(),
  date_of_birth: validateBirthday.required(),
  city_of_birth: yup.string().required(),
  place_of_birth: yup.string().required(),
  country_of_birth: yup.string().required(),
  country_of_birth_id: yup.string().required(),
  national_personal_number: yup.string().required(),
  national_personal_number_type: yup.string().required(),
  passport_document_number: yup.string().required(),
  occupation: yup.string().required(),
  fiscal_code: yup.string().required(),
  signature: yup.string().required(),
  has_single_legal_guardian: yup.boolean(),
};

function isAdult(value: string) {
  const today = parse(
    format(new Date(), 'yyyy-MM-dd'),
    'yyyy-MM-dd',
    new Date()
  );
  const date = parseISO(value);
  if (isValid(date)) {
    const result = differenceInYears(today, date);
    return result >= 18;
  }
  return false;
}

export function validateForm(values, requiredFields = []) {
  const shape = requiredFields.reduce(
    (acc, field) => ({
      ...acc,
      [field]: fieldNameToValidationMap[field],
    }),
    {}
  );
  const validationSchema = yup.object().shape({
    ...shape,
  });

  return (
    validationSchema
      .validate(values)
      // without then it resolves and passes values as error messages
      .then(() => {
        return undefined;
      })
      .catch(err => {
        return yupToFormErrors(err);
      })
  );
}

const PassengerForm = ({
  navigateNext,
  pageType,
  navigateAfterUpdateToAdult,
  passengerId,
  claimId,
}: {
  navigateNext: (disableBackButton: boolean) => void;
  navigateAfterUpdateToAdult?: () => void;
  pageType?: PageType;
  passengerId: string;
  claimId?: string;
}) => {
  // Hooks
  const [handleError] = useHandleError();
  const { formatMessage } = useIntl();
  const { token } = useAuthenticate();

  const [passenger, setPassenger] = useState();
  const [missingFields, setMissingFields] = useState();
  const [initialValues, setInitialValues] = useState<PersonModel>();
  const [isMainBooker, setIsMainBooker] = useState(false);
  /**
   * Easiest solution to refresh component after updating to adult is
   * just using get from hook as it will rerender because context changed
   */
  const { data, isLoaded, loading, get: refetch } = useJsonApi(
    `/passengers/${passengerId}`,
    {
      queryParams: {
        include: [
          'person.country',
          'person.countryOfBirth',
          'person.legalGuardians',
        ].join(),
      },
      cachePolicy: 'no-cache',
    },
    [passengerId]
  );

  useEffect(() => {
    if (isLoaded && data[0]) {
      setPassenger(data[0]);
    }
    // eslint-disable-next-line
  }, [isLoaded, loading]);

  useEffect(() => {
    if (!isEmpty(passenger)) {
      setMissingFields(passenger.person?.missing_fields);
      if (passenger.main_booker) {
        setIsMainBooker(true);
      }

      setInitialValues({
        first_name: passenger.person.first_name,
        last_name: passenger.person.last_name,
        date_of_birth: passenger.person.date_of_birth,
        fiscal_code: passenger.person.fiscal_code,
        country_id: passenger?.person?.countryOfBirth?.id,
        ...(passenger?.person.country_of_birth
          ? { countryOfBirth: passenger?.person?.country?.code }
          : {}),
        ...(passenger?.person.national_personal_number_type
          ? {
              national_personal_number_type:
                passenger?.person.national_personal_number_type,
            }
          : {}),
        ...(passenger?.person.national_personal_number
          ? {
              national_personal_number:
                passenger?.person.national_personal_number,
            }
          : {}),
      });
    }
  }, [passenger]);

  const { navigateNext: navigateToNextPath } = useStepNavigate(
    PathDefs.AccountClaimDocumentsPassengerAuthorization
  );

  const claim = useBuild(
    'claims',
    claimId || localStorage.getItem('yo:claimId')
  );

  // Get the required fields
  const { fields, chunkedFields } = useSortFields(missingFields);

  useEffect(() => {
    if (passenger && claim && !missingFields?.length) {
      navigateToNextPath?.({ passengerData: passenger, guardian: '1' });
    }
    // eslint-disable-next-line
  }, [missingFields?.length]);

  function updateToAdult(values, setSubmitting) {
    handleSubmit(
      values,
      { setSubmitting },
      {
        handleError,
        passenger,
        claim,
        values: {
          ...values,
          is_minor: false,
        },
        token,
        refetch,
        // On documents authorization we just refetch
        navigateNext: navigateAfterUpdateToAdult || refetch,
      }
    );
  }

  const [localStorageValues, setPassengerValues] = useLocalStorageState(
    `passenger-${passengerId}`
  );

  // Remove signature from local storage on unmount
  useEffect(() => {
    return () => {
      if (localStorageValues && !isEmpty(localStorageValues)) {
        const { signature, ...rest } = localStorageValues;
        if (signature) {
          setPassengerValues(rest);
        }
      }
    };
  });

  useEffect(() => {
    validateForm(initialValues, fields);
  }, [initialValues, fields]);

  function onSubmit(values, formik) {
    handleSubmit(values, formik, {
      passenger,
      claim,
      refetch,
      navigateNext:
        pageType === PageType.Funnel
          ? navigateNext
          : () =>
              navigateToNextPath?.({ passengerData: passenger, guardian: '1' }),
      handleError,
      token,
    });
  }

  const isMobile = window.innerWidth < 617;

  return initialValues ? (
    <Formik
      onSubmit={onSubmit}
      validateOnMount={true}
      initialValues={initialValues}
      // eslint-disable-next-line
      validate={validationValues => validateForm(validationValues, fields)}
    >
      {({ values, setSubmitting }) => (
        <Form>
          <Card onlyDesktop={true}>
            <div className="flex justify-center sm:justify-between items-center text-center">
              <StepTitle
                className="mb-0"
                style={{
                  margin: '12px',
                  maxWidth: isMobile ? '60%' : 'none',
                }}
              >
                {passenger.person.is_minor
                  ? formatMessage(messages.sharableClaimPageTitle, {
                      minor: (
                        <span className="text-black">
                          {passenger.person.first_name}{' '}
                          {passenger.person.last_name}
                        </span>
                      ),
                    })
                  : pageType === PageType.Account
                  ? formatMessage(
                      messages.claimAccountGrantPermissionForPassengerHeader,
                      {
                        passenger: (
                          <span className="text-black">
                            {`${passenger.person.first_name} ${passenger.person.last_name}`}
                          </span>
                        ),
                      }
                    )
                  : claim?.product_type === 'flight_compensation'
                  ? isMainBooker
                    ? formatMessage(messages.mainBookerInfoCompensationTitle, {
                        amount: (
                          <span className="text-black">
                            {`€${claim?.compensation_per_passenger?.amount}`}
                          </span>
                        ),
                      })
                    : formatMessage(messages.shareablePassengerPageTitle, {
                        mainbooker: claim?.customer?.firstname,
                        amount: (
                          <span className="text-black">
                            {`€${claim?.compensation_per_passenger?.amount}`}
                          </span>
                        ),
                      })
                  : isMainBooker
                  ? formatMessage(messages.mainBookerInfoTicketRefundTitle)
                  : formatMessage(messages.shareablePassengerRefundPageTitle, {
                      mainbooker: claim?.customer?.firstname,
                    })}
              </StepTitle>
            </div>
            <p className="mb-35 mt-15 mx-10 sm:mx-0 text-black text-center sm:text-left">
              {isMainBooker
                ? formatMessage(messages.mainBookerInfoDescription)
                : passenger.person.is_minor
                ? formatMessage(messages.sharableClaimPageDescription)
                : null}
            </p>
            <Card onlyMobile={true}>
              <h2 className="sm:mx-0 mb-18">
                {passenger.person.is_minor
                  ? formatMessage(messages.fillInMinorDetails)
                  : formatMessage(messages.shareablePassengerPageSubTitle)}
              </h2>
              <PassengerDataForm
                chunkedFields={chunkedFields}
                alertComponent={
                  passenger.person.is_minor &&
                  isAdult(values?.date_of_birth || values?.birthday) ? (
                    <Alert level="warning" className="">
                      {formatMessage(messages.minorBirthdateAlert, {
                        // eslint-disable-next-line
                        cta: (...chunks) => (
                          <span
                            style={{
                              display: 'contents',
                            }}
                            className="link"
                            // eslint-disable-next-line
                            onClick={() => updateToAdult(values, setSubmitting)}
                          >
                            {chunks}
                          </span>
                        ),
                      })}
                    </Alert>
                  ) : null
                }
              >
                {!passenger.person.is_minor &&
                missingFields?.includes('signature') ? (
                  <>
                    <SubTitleWithIcon
                      as="p"
                      icon={UserIcon}
                      className="fill-primary text-gray-300 mb-2 sm:mb-10"
                    >
                      <span
                        data-testid="passenger-name"
                        className="font-bold font-pt-sans text-gray-400"
                      >{`${passenger.person.first_name} ${passenger.person.last_name}`}</span>
                    </SubTitleWithIcon>
                    <Field name="signature" component={SignaturePad} />
                  </>
                ) : null}
              </PassengerDataForm>
              <TrustBox className="my-8" disableClick={true} />
              {isMobile && (
                <StepNavigation
                  submitButtonMessage={formatMessage(messages.submitContinue)}
                  dataLayerPushOnContinue={Boolean(passenger.person.is_minor)}
                  step={passenger.person.is_minor ? steps.Minor : undefined}
                  style={{ margin: '10px' }}
                />
              )}
              {!passenger.is_minor &&
              missingFields?.includes('signature') &&
              isMobile ? (
                <p className="mb-35 mt-15 mx-10 sm:mx-0 text-black">
                  <PermissionDescription
                    placeOfJurisdiction={claim?.placeOfJurisdiction}
                    lettersToSign={passenger?.letters_to_sign}
                    passengerId={passengerId}
                    productType={claim?.product_type}
                  />
                </p>
              ) : null}
            </Card>
            {!passenger.is_minor &&
            missingFields?.includes('signature') &&
            !isMobile ? (
              <p className="mb-35 mt-15 mx-10 sm:mx-0 text-black">
                <PermissionDescription
                  placeOfJurisdiction={claim?.placeOfJurisdiction}
                  lettersToSign={passenger?.letters_to_sign}
                  passengerId={passengerId}
                  productType={claim?.product_type}
                />
              </p>
            ) : null}
          </Card>
          {!isMobile && (
            <StepNavigation
              submitButtonMessage={formatMessage(messages.submitContinue)}
              dataLayerPushOnContinue={Boolean(passenger.person.is_minor)}
              step={passenger.person.is_minor ? steps.Minor : undefined}
            />
          )}
          <FormikPersist localStorageKey={`passenger-${passengerId}`} />
        </Form>
      )}
    </Formik>
  ) : (
    <div className="flex justify-center mt-40">
      <Loading />
    </div>
  );
};

export default PassengerForm;
